import { formatDate } from '@angular/common';
import { Component, computed, effect, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  getFlattenedTowLegs,
  type ITowLogEvent,
  TowLogEventCodes,
  TowLogEventLabels,
  type TTowLogEventCode,
} from '@cpr/shared/tow-log';
import type {
  CellDoubleClickedEvent,
  CellKeyDownEvent,
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  SizeColumnsToContentStrategy,
} from 'ag-grid-community';
import { CustomDateComponent } from '../../../../../shared/components/ag-custom-date-editor/custom-date.component';
import { PermissionsService } from '../../../permissions/permissions.service';
import { TowLogApiService } from '../../../tow-log/services/tow-log-api.service';
import type { TowRouteData } from '../../interface/tow-route-data.interface';
import { DegreesMinutesEditorComponent } from './degrees-minutes-editor.component';
import { TowLogDataTableActionsRendererComponent } from './tow-log-data-table-actions-renderer.component';

@Component({
  selector: 'app-tow-log-data-table',
  templateUrl: './tow-log-data-table.component.html',
})
export class TowLogDataTableComponent implements OnInit {
  private readonly routeData = inject(ActivatedRoute).snapshot.data as TowRouteData;

  readonly tow = this.routeData.tow;
  readonly towLog = this.routeData.towLog;

  readonly cutsGenerated = computed(() => !!this.routeData.samples()?.length);

  @Output() editModeState = new EventEmitter<boolean>();

  private gridAPI: GridApi;

  readonly columnDefs: ColDef[] = [
    {
      field: 'eventDate',
      headerName: 'Date & Time (UTC)',
      cellEditor: CustomDateComponent,
      minWidth: 334,
      valueFormatter: (params) =>
        params.value ? formatDate(params.value, 'dd/MM/y HH:mm', 'en-GB', 'UTC') : '',
    },
    {
      field: 'eventCode',
      headerName: 'Type',
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: TowLogEventCodes },
      refData: TowLogEventLabels,
      minWidth: 160,
    },
    {
      field: 'eventLatitude',
      headerName: 'Latitude',
      cellEditor: DegreesMinutesEditorComponent,
      cellEditorParams: {
        direction: 'lat',
      },
      cellRenderer: ({ value }: ICellRendererParams) => {
        if (value) {
          return this.towLogs.getDegreesMinutesString(value, 'lat');
        }
      },
      minWidth: 150,
    },
    {
      field: 'eventLongitude',
      headerName: 'Longitude',
      cellEditor: DegreesMinutesEditorComponent,
      cellEditorParams: {
        direction: 'lng',
      },
      cellRenderer: ({ value }: ICellRendererParams) => {
        if (value) {
          return this.towLogs.getDegreesMinutesString(value, 'lng');
        }
      },
      minWidth: 150,
    },
    {
      field: 'course',
      editable: false,
      valueFormatter: ({ value }) => value?.toFixed(),
    },
    {
      field: 'speed',
      editable: false,
      valueFormatter: ({ value }) => value?.toFixed(2),
    },
    {
      field: 'distance',
      editable: false,
      valueFormatter: ({ value }) => value?.toFixed(2),
    },
    {
      field: 'comments',
      flex: 1,
    },
    {
      cellRenderer: TowLogDataTableActionsRendererComponent,
      cellRendererParams: {
        isEditMode: (() => this.edit).bind(this),
        onDelete: this.onDelete.bind(this),
      },
      type: 'rightAligned',
      width: 90,
      editable: false,
    },
  ];

  readonly autoSizeStrategy: SizeColumnsToContentStrategy = {
    type: 'fitCellContents',
    // Any cols without a size goal will autosize to cell contents
    colIds: this.columnDefs.filter((def) => !def.flex && !def.width).map(({ field }) => field),
  };

  edit = false;
  changesMade = false;
  hasPermission = false;
  defaultColDef: ColDef = {
    sortable: false,
  };
  gridOptions: GridOptions = {
    singleClickEdit: true,
    onRowDataUpdated: () => {
      let a = JSON.stringify(this.towLog());
      let b = JSON.stringify(this.rowData);
      if (a !== b) {
        this.towLog.set(this.rowData as never);
      }
    },
  };
  initialStringifiedValue: string;
  defaultStringifiedValue: string;
  rowData: Partial<ITowLogEvent<string, Date>>[];

  constructor(
    private readonly permissions: PermissionsService,
    private readonly towLogs: TowLogApiService,
  ) {}

  async ngOnInit() {
    this.hasPermission = await this.permissions.hasPermission('towLog', 'edit');
    this.editModeState.emit(this.edit);
  }

  ngOnDestroy() {
    this.onTowLogChange.destroy();
  }

  private once = false;
  private readonly onTowLogChange = effect(() => {
    const towLog = this.towLog();
    if (towLog) {
      const towLegs = getFlattenedTowLegs(towLog);
      if (!this.once) {
        this.initialStringifiedValue = JSON.stringify(towLegs);
        this.once = true;
      }
      this.rowData = towLegs;
      this.checkChangesMade();
    }
  });

  onGridReady({ api }: GridReadyEvent) {
    this.gridAPI = api;
  }

  onKeyDown(event: CellKeyDownEvent) {
    const keyEvent = event.event as KeyboardEvent;
    if (keyEvent.key === 'Enter' && keyEvent.shiftKey) {
      this.insertRow();
    }
  }

  onDoubleClick({ rowIndex, column }: CellDoubleClickedEvent) {
    if (!this.edit) {
      this.startEdit();
      setTimeout(() => {
        this.gridAPI.startEditingCell({ rowIndex, colKey: column });
      });
    }
  }

  cancelEdit() {
    this.edit = false;
    this.defaultColDef = { ...this.defaultColDef, editable: false };
    this.reset();
    this.editModeState.emit(this.edit);
  }

  checkChangesMade() {
    this.changesMade = JSON.stringify(this.rowData) !== this.initialStringifiedValue;
  }

  insertRow() {
    const lastEventCode = this.rowData.length ? this.rowData.at(-1).eventCode : null;

    let eventCode: TTowLogEventCode = this.rowData.length ? 'COURSE_CHANGE' : 'SHOOT';
    let editIndex = this.rowData.length;

    if (lastEventCode === 'HAUL') {
      this.rowData = [
        ...this.rowData.slice(0, -1),
        { towId: this.tow()._id, eventCode },
        this.rowData.at(-1),
      ];
      editIndex--;
    } else {
      if (this.rowData.length && this.rowData.at(-1).eventCode === 'MIDHAUL') {
        eventCode = 'MIDSHOOT';
      }
      this.rowData = [...this.rowData, { towId: this.tow()._id, eventCode }];
    }

    this.checkChangesMade();

    setTimeout(() => {
      this.gridAPI.startEditingCell({ rowIndex: editIndex, colKey: 'eventDate' });
    });
  }

  reset() {
    if (this.initialStringifiedValue === this.defaultStringifiedValue) {
      this.rowData = [];
    } else {
      this.towLog.set((this.rowData = JSON.parse(this.initialStringifiedValue)));
    }

    this.changesMade = false;
  }

  async save() {
    await this.towLogs.updateForTow(this.tow()._id, this.rowData);
    this.towLog.refresh();
    this.cancelEdit();
  }

  startEdit() {
    this.edit = true;
    this.defaultColDef = { ...this.defaultColDef, editable: true };
    if (!this.rowData.length) {
      this.insertRow();
      this.defaultStringifiedValue = JSON.stringify(this.rowData);
    }
    this.initialStringifiedValue = JSON.stringify(this.rowData);
    this.editModeState.emit(this.edit);
  }

  onDelete(rowNumber: number) {
    this.rowData = this.rowData.filter((_, i) => i !== rowNumber);
    this.checkChangesMade();
  }
}
